import React, { useEffect } from "react";
import BarChart from "./Chart"; // Assuming you have this import for the Chart component
import { ReactSVG } from "react-svg";
import selfSvg from "../../img/self-cropped.svg";
import {
  RiSparkling2Fill, RiDownload2Fill, RiUpload2Fill, RiArrowRightSLine, RiArrowDownSLine,
} from "react-icons/ri";
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Link,
  VStack,
  Text,
  PopoverTrigger,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  PopoverContent,
  Popover, Portal, PopoverAnchor,
  Button
} from "@chakra-ui/react";
import {cqAPI, getBid} from "../../lib/Auth";
import { sanitizeChatHtml } from "../../lib/sanitizeChatHtml";
import { useAppContext } from "../../AppContext";
import CampaignSummary from "./CampaignSummary";

const downloadableBuffers = ['pdf-file', 'csv-file'];

function parseTable(htmlStr, just_verify) {
  // Extract headers using regex. Cells may carry attributes (the assistant
  // writes e.g. <th scope="col">), so read past them instead of skipping the
  // whole table and leaving the chart out.
  const headerRegex = /<th\b[^>]*>(.*?)<\/th>/g;
  let match;
  const headers = [];
  while ((match = headerRegex.exec(htmlStr))) {
    headers.push(match[1]);
  }

  // Extract rows using regex
  const rowRegex = /<tr\b[^>]*>([\s\S]*?)<\/tr>/g; // Using [\s\S] to match across lines
  const rows = [];
  let rowData;
  while ((rowData = rowRegex.exec(htmlStr))) {
    const cells = [];
    const cellRegex = /<td\b[^>]*>(.*?)<\/td>/g;
    let cellMatch;
    while ((cellMatch = cellRegex.exec(rowData[1]))) {
      cells.push(cellMatch[1]);
    }
    if (cells.length) {
      rows.push(cells);
    }
  }

  let inValidData = false;

  // Create the resulting structure
  const columnData = [];
  for (let i = 1; i < headers.length; i++) {
    const data = rows.map((row) => {
      const value =
        row[i] && row[i].replace("%", "").replace("$", "").replace(/\,/g, "");
      if (isNaN(String(value))) {
        inValidData = true;
      }
      return [row[0], isNaN(value) ? value : parseFloat(value)];
    });
    columnData.push({ key: headers[i], data: data });
  }
  if (just_verify) {
    // Cells the regexes above cannot read (attributes on <th>/<td>, nested
    // markup, ...) leave nothing to plot, and charting that anyway drew an
    // empty, padded chart container — a blank band, which is exactly what
    // this path is supposed to avoid.
    return !inValidData && columnData.length > 0 && rows.length > 0;
  }
  return columnData;
}

/**
 * A call review reads whole transcripts, so it can take minutes and it keeps going when
 * the office leaves the page. What the office sees meanwhile (SS-4110) is the turn's own
 * bubble ("Reading all 20 matching calls. This keeps running if you leave this page…") and
 * ONE progress panel under it that fills in as each batch of calls comes back. There is no
 * separate announcement banner: it said the same thing twice.
 */

/**
 * A call link the app can actually open: this practice's call report, and nothing else.
 *
 * The server supplies the same path, but a link is only used when it IS that route — a
 * stale or external URL in a finding must never become a link in the chat.
 */
const CALL_REPORT_ROUTE = "/reports/calls/";

/** The route this app owns, with ONE id segment — after the browser has normalised it. */
const isCallReportPath = (link) => {
  if (typeof link !== "string" || !link.startsWith(CALL_REPORT_ROUTE)) return false;
  try {
    const { pathname } = new URL(link, window.location.origin);
    const rest = pathname.startsWith(CALL_REPORT_ROUTE) ? pathname.slice(CALL_REPORT_ROUTE.length) : null;
    // "/reports/calls/../../settings" normalises out of the route; so does an extra segment.
    return !!rest && !rest.includes("/");
  } catch (e) {
    return false;
  }
};

const callReportLink = (item) => {
  if (isCallReportPath(item?.link)) return item.link;
  const bid = getBid();
  return `${CALL_REPORT_ROUTE}${item?.call_id}${bid ? `?bid=${bid}` : ""}`;
};

const REVIEW_TERMINAL_STATUSES = ["complete", "cancelled", "failed"];

const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs - mins * 60}s`;
};

/** The one-line summary of a finished report: what was done, how many steps, how long. */
const summarizeActivity = (activity) => {
  const parts = [];
  const review = activity?.review;
  if (review) {
    const done = review.done ?? 0;
    const total = review.total ?? 0;
    if (review.job_status === "cancelled") parts.push(`Stopped after ${done} of ${total} call${total === 1 ? "" : "s"}`);
    else if (review.job_status === "failed") parts.push("The calls could not be read");
    else parts.push(`Read ${done} of ${total} call${total === 1 ? "" : "s"}`);
  }
  const steps = (activity?.steps || []).filter((s) => s.stage !== "composing");
  if (steps.length) parts.push(`${steps.length} step${steps.length === 1 ? "" : "s"}`);
  const duration = activity?.startedAt && activity?.endedAt ? formatDuration(activity.endedAt - activity.startedAt) : null;
  if (duration) parts.push(duration);
  if (!parts.length) {
    if (activity?.status === "cancelled") return "Stopped";
    if (activity?.status === "failed") return "Did not finish";
    return "Done";
  }
  // A question that did not finish says so first (a review's own line already does).
  if (!review && activity?.status === "cancelled") parts.unshift("Stopped");
  else if (!review && activity?.status === "failed") parts.unshift("Did not finish");
  return parts.join(" · ");
};

/** A step's own detail lines, when it has any. */
function StepDetail({ detail }) {
  if (!detail) return null;
  return (
    <>
      {detail.summary && <Text fontSize="xs" color="gray.600">{detail.summary}</Text>}
      {Array.isArray(detail.collections) && detail.collections.length > 0 && (
        <Text fontSize="xs" color="gray.600">Looked at: {detail.collections.join(", ")}</Text>
      )}
      {detail.assumptions && <Text fontSize="xs" color="gray.600">Assumed: {detail.assumptions}</Text>}
    </>
  );
}

/**
 * The activity report of a turn (SS-4110): what the assistant is doing — or did — for a
 * question, the way a chat assistant shows its work. Collapsed to one line: the current
 * step while it runs ("Looking into your practice database…", "Analysed 8 of 40 calls"),
 * a summary once it is done ("Read 40 of 40 calls · 3 steps · 42s"). Expanded: every step
 * with what it found, and a call review's per-call findings with links. It collapses
 * itself when the turn finishes and stays expandable in history. A review still reading
 * offers its own Stop, scoped to that review.
 */
function ActivityReport({ activity, pending, onStop }) {
  const [expanded, setExpanded] = React.useState(false);
  const steps = activity?.steps || [];
  const review = activity?.review || null;
  const status = activity?.status || null;
  const running = !!pending || status === "running";
  React.useEffect(() => {
    if (!running) setExpanded(false);
  }, [running]);

  if (!steps.length && !review && !running) return null;

  const reviewRunning = !!review && !REVIEW_TERMINAL_STATUSES.includes(review.job_status) && !activity?.reviewAnswered;
  const lastStep = steps.length ? steps[steps.length - 1] : null;
  const latest = review && (reviewRunning || !lastStep)
    ? (review.label || `Analysed ${review.done ?? 0} of ${review.total ?? 0} calls`)
    : (lastStep?.label || null);
  const header = running ? (latest || "Working on it…") : summarizeActivity(activity);
  const items = review?.items || [];
  const skipped = review?.skipped || [];

  return (
    <Box className="activity-report" data-testid="activity-report" w="100%" mb={2}>
      <HStack spacing={3} align="center" {...(running ? { "data-testid": "turn-pending" } : {})}>
        <HStack
          as="button"
          type="button"
          spacing={2}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          data-testid="review-progress-toggle"
        >
          <Icon as={expanded ? RiArrowDownSLine : RiArrowRightSLine} boxSize={5} />
          {running && <span className="spinner activity-spinner" aria-hidden="true" />}
          <Text as="b" fontSize="sm">{header}</Text>
        </HStack>
        {reviewRunning && onStop && review.job_id && (
          <Button
            size="xs"
            variant="outline"
            onClick={() => onStop(review.job_id)}
            data-testid="review-progress-stop"
          >
            Stop reading
          </Button>
        )}
      </HStack>
      {expanded && (
        <VStack align="start" spacing={2} pl={6} pt={2} data-testid="review-progress-details">
          {steps.map((step, index) => (
            <Box key={step.seq ?? `${step.stage}-${index}`} data-testid="activity-step">
              <Text fontSize="sm">
                {step.label}
                {step.done === false ? "…" : ""}
                {Number.isFinite(step.elapsed_ms) && step.elapsed_ms > 0 ? ` · ${formatDuration(step.elapsed_ms)}` : ""}
              </Text>
              <StepDetail detail={step.detail} />
            </Box>
          ))}
          {review && (
            <Box className="review-progress" data-testid="review-progress" w="100%">
              <Text as="b" fontSize="sm">{review.label || `Analysed ${review.done ?? 0} of ${review.total ?? 0} calls`}</Text>
              {items.map((item) => (
                <Box key={String(item.call_id)} pl={2}>
                  <Link href={callReportLink(item)} target="_blank" rel="noopener noreferrer">
                    {item.date ? new Date(item.date).toLocaleString() : `Call ${item.call_id}`}
                    {item.extension === null || item.extension === undefined ? '' : ` — extension ${item.extension}`}
                  </Link>
                  {item.headline && <Text>{item.headline}</Text>}
                  {item.detail && <Text fontSize="sm">{item.detail}</Text>}
                </Box>
              ))}
              {skipped.map((item) => (
                <Text key={`skipped-${String(item.call_id)}`} fontSize="sm" pl={2}>
                  Call {String(item.call_id)} was not read: {item.reason}
                </Text>
              ))}
              {items.length === 0 && skipped.length === 0 && <Text fontSize="sm" pl={2}>No findings yet.</Text>}
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
}

function MessageList({
  messages,
  chatBoxRef,
  promptSuggestions = [],
  fillQuestion,
  disableSendButton,
  longProcessing,
  uploadCSVFile,
  onNextClick,
  showCampaignSummary = false,
  campaigns = [],
  onCampaignUpdate,
  onSendTest,
  onCampaignBack,
  onScheduleSuccess,
  onStopReview,
}) {
  useEffect(() => {
    if (chatBoxRef.current) {
      const scrollHeight = chatBoxRef.current.scrollHeight;
      chatBoxRef.current.scrollTop = scrollHeight;
    }
  }, [messages, chatBoxRef]);

  // No provider (isolated render in tests) reads as unknown, and unknown fails OPEN:
  // only a business KNOWN to have support chat off gets the inline note below.
  const { selectedBusiness } = useAppContext() || {};

  // Detect support-help elements in rendered HTML and open the support chat widget
  useEffect(() => {
    if (!chatBoxRef.current) return;
    const elements = chatBoxRef.current.querySelectorAll('.support-help:not([data-handled])');
    elements.forEach((el) => {
      const question = el.getAttribute('data-question');
      el.setAttribute('data-handled', 'true');
      if (question) {
        // A practice with support chat disabled mounts no widget, so the event below
        // has no listener — the assistant says "connecting you with support" and
        // nothing happens, the same dead end the disabled Intercom messenger used to
        // produce. Say so inline instead of doing nothing.
        if (selectedBusiness && selectedBusiness.support_chat !== true) {
          el.textContent = "Support chat isn't available for this practice.";
          return;
        }
        try {
          window.dispatchEvent(new CustomEvent('viva:open-support-chat', { detail: { question } }));
        } catch (e) {
          console.error("Failed to open support chat:", e);
        }
      }
    });
  }, [messages, selectedBusiness]);

  const hasCompleteTable = (content) => {
    return (
      content &&
      content.includes("<table") &&
      content.includes("</table>") &&
      parseTable(content, true)
    );
  };
  
  const getFileDescription = (buffer) => {
    switch (buffer.type) {
      case 'pdf-file':
        return 'PDF';
      case 'csv-file':
        return 'CSV';
    }
  }

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return;
      }

      // Create a temporary link element and trigger the download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName); // Set the file name for the download
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the blob URL and removing the temporary link
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <div className="message-list" ref={chatBoxRef} style={messages.length === 0 ? { display: 'flex', flexDirection: 'column' } : undefined}>
      {messages.length === 0 ? (
        <div className="empty-message-list" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          {disableSendButton && (
            <div
              className="spinner"
              style={{ position: "absolute", bottom: "0px" }}
            ></div>
          )}
          {promptSuggestions.length > 0 && (
            <div className="prompt-suggestions">
              {promptSuggestions.map((suggestion, index) => {
                const categorySlugMap = {
                  'Ask a question': 'ask-question',
                  'Create a patient outreach': 'patient-outreach',
                  'Look up patient': 'look-up-patient',
                };
                const categoryClass = suggestion.category
                  ? `category-${categorySlugMap[suggestion.category] || 'ask-question'}`
                  : `severity-${(suggestion.severity || 'low').toLowerCase()}`;
                return (
                  <div
                    key={index}
                    className={`prompt-suggestion ${categoryClass}`}
                    onClick={() => fillQuestion(suggestion.prompt)}
                  >
                    <div className="prompt-title">{suggestion.title}</div>
                    <div className="prompt-content">{suggestion.prompt}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          {messages.map((message, index) => {
            if (message.type === "loading") {
              if (index !== messages.length - 1) return null;
              return (
                <div key={index} className="message server">
                  <div className="spinner"></div>
                </div>
              );
            }

            // A review's announcement is folded into the turn's own bubble and its report;
            // nothing is painted for it (never an empty <div class="message job_created">).
            if (message.type === "job_created") return null;

            // A progress panel with no turn to hang on (an entry loaded without its id by an
            // older api): shown as a report of its own.
            if (message.type === "progress") {
              return (
                <div key={index} className="message server">
                  <Box mr={4} className="sender-title">
                    <Avatar size="md" bg={""} icon={<Icon as={RiSparkling2Fill} boxSize={9} />} />
                  </Box>
                  <ActivityReport
                    activity={{ steps: [], review: message.content, status: REVIEW_TERMINAL_STATUSES.includes(message.content?.job_status) ? message.content.job_status : "running", reviewAnswered: false }}
                    pending={false}
                    onStop={onStopReview}
                  />
                </div>
              );
            }

            // For other types, display the message content. The HTML comes
            // from the LLM (or straight from the user), so sanitize it before
            // it is injected — and chart from the same sanitized markup the
            // reader sees.
            const safeContent = sanitizeChatHtml(message.content);

            return (
              <React.Fragment key={index}>
                <div className={`message ${message.type}`}>
                  {message.type === "server" && (
                    <Box mr={4} className="sender-title">
                      <Avatar size="md"
                              bg={""}
                              icon={<Icon as={RiSparkling2Fill} boxSize={9} />}
                      />
                    </Box>
                  )}
                  {message.type === "user" && (
                    <ReactSVG src={selfSvg} className="sender-title" />
                  )}
                  {message.type === "server" && (message.activity || message.pending) && (
                    <ActivityReport activity={message.activity || null} pending={!!message.pending} onStop={onStopReview} />
                  )}
                  <div dangerouslySetInnerHTML={{ __html: safeContent }} />
                  {hasCompleteTable(safeContent) && (
                    <BarChart data={parseTable(safeContent)} />
                  )}
                  {(message?.r_buffers || []).filter(b => downloadableBuffers.includes(b.type)).length > 0 && (
                    <Flex key={`message-${index}`} direction="column">
                      <VStack align="start">
                        {index === ((messages?.length ?? 0) - 1) && (
                          <Box pt="4">
                            <Text as='b'>
                              Download and check the list before continuing with AI patient outreach.
                            </Text>
                            <Text>
                              You can edit and upload the list if needed.
                            </Text>
                          </Box>
                        )}
                        <Box>
                          <Popover placement="bottom-start">
                            <PopoverAnchor>
                              <HStack align="start" spacing={3}>
                                <Box>
                                  <PopoverTrigger>
                                    <Icon as={RiDownload2Fill} boxSize={4} cursor="pointer"/>
                                  </PopoverTrigger>
                                </Box>
                                {index === ((messages?.length ?? 0) - 1) && (
                                  <Box>
                                    <Icon as={RiUpload2Fill} boxSize={4} cursor="pointer" onClick={() => uploadCSVFile()}/>
                                  </Box>
                                )}
                              </HStack>
                            </PopoverAnchor>
                            <Portal>
                              <PopoverContent>
                                <PopoverBody>
                                  <VStack w={40}>
                                    {message.r_buffers.filter(b => downloadableBuffers.includes(b.type)).map((buffer, bufferIndex) => (
                                      <Link
                                        key={`${buffer.fileName}-${bufferIndex}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          downloadFile(buffer.url, buffer.fileName);
                                        }}
                                      >
                                          Download {getFileDescription(buffer)}
                                      </Link>
                                    ))}
                                  </VStack>
                                </PopoverBody>
                              </PopoverContent>
                            </Portal>
                          </Popover>
                        </Box>
                      </VStack>
                    </Flex>
                  )}
                  {index === ((messages?.length ?? 0) - 1) && campaigns && campaigns.length > 0 && !showCampaignSummary && !disableSendButton && (
                    <Box pt="2">
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={onNextClick}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </div>
                {longProcessing && message.type === "server" && (
                  <div className="spinner"></div>
                )}
              </React.Fragment>
            );
          })}
          {showCampaignSummary && campaigns.length > 0 && !disableSendButton && (
            <div className="message server">
              <Box mr={4} className="sender-title">
                <Avatar size="md"
                        bg={""}
                        icon={<Icon as={RiSparkling2Fill} boxSize={9} />}
                />
              </Box>
              <CampaignSummary
                campaigns={campaigns}
                onCampaignUpdate={onCampaignUpdate}
                onSendTest={onSendTest}
                onBack={onCampaignBack}
                onScheduleSuccess={onScheduleSuccess}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MessageList;
